import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import {
  formatInCurrency,
  formatJpy,
  formatPlain,
  formatSignedJpy,
  type AmountUnit,
} from '../lib/amount-format'

interface AmountUnitContextValue {
  unit: AmountUnit
  setUnit: (unit: AmountUnit) => void
}

const AmountUnitContext = createContext<AmountUnitContextValue | null>(null)

const STORAGE_KEY = 'zeni_amount_unit'

export function AmountUnitProvider({ children }: { children: ReactNode }) {
  const [unit, setUnitState] = useState<AmountUnit>(() =>
    localStorage.getItem(STORAGE_KEY) === 'man' ? 'man' : 'yen',
  )

  const setUnit = useCallback((next: AmountUnit) => {
    localStorage.setItem(STORAGE_KEY, next)
    setUnitState(next)
  }, [])

  const value = useMemo(() => ({ unit, setUnit }), [unit, setUnit])

  return <AmountUnitContext.Provider value={value}>{children}</AmountUnitContext.Provider>
}

export function useAmountUnit() {
  const ctx = useContext(AmountUnitContext)
  if (!ctx) throw new Error('useAmountUnit must be used within AmountUnitProvider')
  return ctx
}

/** 表示単位の設定を織り込んだフォーマッタ群 */
export function useAmountFormat() {
  const { unit } = useAmountUnit()
  return useMemo(
    () => ({
      unit,
      formatJPY: (amount: number) => formatJpy(amount, unit),
      formatSignedJPY: (amount: number) => formatSignedJpy(amount, unit),
      formatPlain: (amount: number) => formatPlain(amount, unit),
      formatCurrency: (amount: number, currency: string) => formatInCurrency(amount, currency, unit),
    }),
    [unit],
  )
}
